/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import type { UseQueryResult, UseMutationResult } from 'react-query';
import { useQuery, useMutation } from 'react-query';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import type { Site } from '@hotjar/site';
import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useCurrentSite } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';

export interface FirstRecordingsData {
  first_recording_present?: boolean;
  notification_sent?: boolean;
  recording_mode?: string;
}

interface GetRecordingsPopupStatusParams {
  queryKey: [string, { siteId: number }];
}

export const getRecordingsPopupStatus = async ({
  queryKey,
}: GetRecordingsPopupStatusParams): Promise<FirstRecordingsData> => {
  const [, { siteId }] = queryKey;

  const { data } = await axios.get(`/api/v1/recordings/${siteId}/seen_first`);

  return data;
};

export const useSelectRecordingsPopupStatus = (): UseQueryResult<FirstRecordingsData, Error> => {
  const currentSite = useCurrentSite().data;
  const currentUser = useCurrentUser().data;

  return useQuery(
    ['firstRecordingStatus', { siteId: currentSite?.id, userId: currentUser?.id }],
    getRecordingsPopupStatus,
    {
      refetchOnWindowFocus: false,
      enabled: !!currentSite?.id && !!currentUser?.id,
      notifyOnChangeProps: ['data', 'error', 'isLoading'],
      cacheTime: 1000 * 60 * 60 * 24, // 24h
      staleTime: 1000 * 60 * 60 * 24, // 24h
    },
  );
};

interface NotificationSentData {
  site: Site;
  user: { id: number };
}

export const useSetNotificationSent = (): UseMutationResult<
  ApiBaseResponse,
  AxiosResponse,
  NotificationSentData
> => {
  return useMutation(({ site }) => axios.post(`/api/v1/recordings/${site.id}/seen_first`), {
    onMutate: async ({ site, user }) => {
      const newNotificationSent = {
        notification_sent: true,
      };

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries([
        'firstRecordingStatus',
        { siteId: site.id, userId: user.id },
      ]);

      // Snapshot the previous value
      const prevValues = queryClient.getQueryData<FirstRecordingsData>([
        'firstRecordingStatus',
        { siteId: site.id, userId: user.id },
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(['firstRecordingStatus', { siteId: site.id, userId: user.id }], {
        ...prevValues,
        ...newNotificationSent,
      });

      // Return a context object with the snapshotted value
      return { ...prevValues, ...newNotificationSent };
    },
  });
};

interface UseFirstRecordingStatus {
  isIntervalEnabled?: boolean;
}

export const useFirstRecordingStatus = ({
  isIntervalEnabled,
}: UseFirstRecordingStatus): UseQueryResult<FirstRecordingsData, Error> => {
  const currentSite = useCurrentSite().data;
  const currentUser = useCurrentUser().data;
  const { getState } = useTrackingCode();
  const trackingCodeState = getState(currentSite?.lastData);
  const [isEnabled, setIsEnabled] = useState(trackingCodeState?.isActive);

  useEffect(() => {
    setIsEnabled(trackingCodeState?.isActive);
  }, [trackingCodeState?.isActive]);

  return useQuery(
    ['firstRecordingStatus', { siteId: currentSite?.id, userId: currentUser?.id }],
    getRecordingsPopupStatus,
    {
      refetchOnWindowFocus: false,

      onSuccess: ({ first_recording_present }) => {
        if (first_recording_present) {
          setIsEnabled(false);
        }
      },
      notifyOnChangeProps: ['data', 'error', 'isLoading'],
      enabled: !!currentSite?.id && !!currentUser?.id && trackingCodeState?.isActive && isEnabled,
      refetchInterval: isIntervalEnabled ? 5000 : false,
      refetchIntervalInBackground: true,
    },
  );
};
